import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import {
  CompactPeoplePicker,
  FocusZoneDirection,
  FocusZoneTabbableElements,
  IBasePickerSuggestionsProps,
  IPersonaProps,
  IPickerItemProps,
  ISuggestionItemProps,
  IconButton,
  Icon as OfficeIcon,
  Persona,
  PersonaSize,
} from "@fluentui/react";
import { ErrorRecord, Label, Text, useTheme } from "cayo.ui";
import {
  Entity,
  Icon as IconAnnotation,
  PossibleValueItem,
  PropertyAnnotation,
  PropertyIcon,
} from "@cayo/shared/api/types/cayo-graph";
import { Column, Row } from "@cayo/shared/components/CommonLayout";
import { withHidden } from "@cayo/shared/components/Hoc/withHidden";
import { IGetIconAnnotation } from "@cayo/shared/hooks/useTypeAnnotations";
import logger from "@cayo/shared/libs/logger";
import { commonMessages } from "@cayo/shared/messages/common";
import { IPickerItem } from "./IPickerItem";
import { fetchObjects, getTextFromItem, validateInput } from "./logic";
import oDataConverters from "./odata-converters";
import { mapPropsToSearchScopes } from "./search-scopes-mapper";
import { NoLabel, PersonaContent, PickerWrapper, StyledColumn, StyledFocusZone } from "./style";
import { usePickerObjects } from "./usePickerObjects";
import useScopes from "./useScopes";
import { IObjectPicker } from "../../api/schema.api";
import { useDialogService } from "../../services/Dialogs/dialog-context";
import { DIALOG_IDS } from "../../services/Dialogs/dialog-ids";
import arrayUtils from "../../utils/array-utils";
import Error from "@cayo/shared/components/common/Error";
import Icon from "../Graphics/Icon";
import { IFormComponent } from "../QueryFilterForm/IFormComponent";

const typeInAllowTypes: Array<string> = [
  "targetObjectName",
  "targetObjectName_exclude",
  "initiatorName",
  "initiatorName_exclude",
  "targetObjectLocations",
  "targetObjectLocations_exclude",
];

const log = logger.getLogger("ObjectPicker");

export type ObjectPickerProps = Omit<IObjectPicker, "type"> &
  IFormComponent & {
    value: IPickerItem[];
    hideMore?: boolean;
    showClearButton?: boolean;
    hideModificationTimeColumn?: boolean | undefined;
    hideObjectTypeIcon?: boolean | undefined;
    error?: ErrorRecord;
    possibleValues?: PossibleValueItem[];
    offBlurAction?: boolean;
  } & {
    getIconAnnotation: IGetIconAnnotation;
    propertyAnnotations?: PropertyAnnotation[];
    targetType?: string;
  };

const getDisplayText = (v: any, item: Omit<IObjectPicker, "type">): string => {
  const { selectPropertyName, displayNameProp, displayNameAlternateProp } = item;
  const result = (v as any).displayName || (v as any).DisplayName || v.objectName;

  if (result) {
    return result;
  }

  if (selectPropertyName && (v as any)[selectPropertyName]) {
    return (v as any)[selectPropertyName];
  }
  if (displayNameProp && (v as any)[displayNameProp]) {
    return (v as any)[displayNameProp];
  }
  if (displayNameAlternateProp && (v as any)[displayNameAlternateProp]) {
    return (v as any)[displayNameAlternateProp];
  }
  return "Unknown";
};

const ObjectPicker: FC<ObjectPickerProps> = (props) => {
  const showTypeIn = typeInAllowTypes.includes(props?.name || "");
  const { cayoTheme, officeTheme } = useTheme();
  const intl = useIntl();
  const { openById, closeById } = useDialogService();

  const [mostRecentlyUsed, setMostRecentlyUsed] = useState<IPickerItem[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [lastInput, setLastInput] = useState("");
  const {
    getIconAnnotation,
    propertyAnnotations,
    clientBehavior,
    columns,
    readOnly,
    label,
    renderInput,
    displayNameProp,
    displayNameAlternateProp,
    valueTemplate,
  } = props;

  useEffect(() => {
    return () => {
      closeById(DIALOG_IDS.OBJECT_PICKER_DIALOG);
    };
  }, [closeById]);

  const picker = useRef(null);
  const {
    queryPath,
    scopes,
    isLoading: isScopeLoading,
    selectedScopeKey,
  } = useScopes(props.queryPath, props.scope);

  const path = props.path ? "v0/" + props.path : undefined;

  const suggestionProps = useMemo<IBasePickerSuggestionsProps>(() => {
    return {
      noResultsFoundText: props.queryPath && intl.formatMessage(commonMessages.noResults),
      loadingText: intl.formatMessage(commonMessages.loading),
      showRemoveButtons: false,
    } as IBasePickerSuggestionsProps;
  }, []);

  const fetchPickerObjects = usePickerObjects(fetchObjects, columns, clientBehavior);

  const onFilterChanged = useCallback(
    (
      filterText: string,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      currentPersonas: IPickerItem[],
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      limitResults?: number
    ): IPersonaProps[] | Promise<IPersonaProps[]> => {
      log.debug("onFilterChanged -> filterText", filterText);
      const actualQueryPath = path || queryPath;
      if (isLoading || isScopeLoading) {
        log.debug("onFilterChanged -> isLoading || isScopeLoading", isLoading || isScopeLoading);
        return [];
      }

      let selectPropertyName = props.selectPropertyName;
      let possibleValues: PossibleValueItem[] | undefined;
      let iconAnnotations: PropertyIcon[] | undefined;
      if (!actualQueryPath) {
        log.debug("onFilterChanged loadPossibleValues");
        const suffix = "/" + props.name;
        const target = props.targetType + "/" + props.name;
        const propAnnotations =
          propertyAnnotations?.find((a) => a.target === target) ||
          propertyAnnotations?.find((a) => a.target?.endsWith(suffix));
        possibleValues = propAnnotations?.possibleValues ?? props.possibleValues;

        if (!possibleValues?.length) {
          log.debug("onFilterChanged: possibleValues is empty");
          return [];
        }

        iconAnnotations = propAnnotations?.icons;

        possibleValues?.sort(function (a, b) {
          const aName = a.name ?? "";
          const bName = b.name ?? "";
          if (aName > bName) {
            return 1;
          }
          if (aName < bName) {
            return -1;
          }
          return 0;
        });

        selectPropertyName = "id";
        log.debug("onFilterChanged -> possibleValues", possibleValues);
      }

      const fetchFunc = actualQueryPath
        ? fetchPickerObjects({
            path: actualQueryPath,
            searchText: filterText,
            orderBy: props?.defaultOrder?.length ? props?.defaultOrder : undefined,
            desc: undefined,
            filter: typeof props?.filter === "string" && !path ? props.filter : undefined,
            select: undefined,
            expand: undefined,
            additionalFilter:
              props.name === "nestedType" && filterText
                ? ` or contains(displayName,'${filterText}')`
                : "",
            body: props.body,
            operator: props.queryOperator,
          })
        : Promise.resolve(
            possibleValues
              ?.map<Entity & { icon?: IconAnnotation }>((v) => ({
                objectName: v.name,
                id: v.value,
                icon: iconAnnotations?.find((i) => i.key === v.value),
              }))
              .filter((pv) => {
                if (!filterText) {
                  return true;
                } else {
                  return pv.objectName
                    ?.toLocaleLowerCase()
                    .includes(filterText.toLocaleLowerCase());
                }
              })
          );

      return fetchFunc
        .then((response) => {
          if (response?.length) {
            const nonSelectedItems = props.value
              ? response.filter((r) => {
                  const rr = r as any;
                  const name = selectPropertyName ? rr[selectPropertyName!] : r.id;
                  if (!name) {
                    return false;
                  }

                  return props.value?.findIndex((p) => p.id === name) < 0;
                })
              : response;

            let dataResult = nonSelectedItems.map<IPersonaProps>((v) => {
              const result: IPickerItem = {
                id: v.id!,
                text: getDisplayText(v, props),
                declaringType: v.declaringType,
                syntax: v.syntax,
                possibleValues: v.possibleValues,
                queryOperators: v.queryOperators,
                locationType: v.locationType,
                objectName: v.objectName,
                objectType: v.objectType,
                name: v.name,
                icon: (v as any)?.icon as IconAnnotation,
                object: valueTemplate ? v : undefined,
                link: valueTemplate
                  ? oDataConverters.objectToPickerValue(
                      valueTemplate,
                      v,
                      displayNameProp,
                      displayNameAlternateProp
                    )
                  : selectPropertyName
                    ? v[selectPropertyName]
                    : oDataConverters.entityToSoftLink(v),

                onRenderPersonaCoin: () => {
                  const icon = (v as any)?.icon as IconAnnotation;
                  if (
                    v.objectType === "cayo.graph.objectTypeElement" ||
                    v.objectType === "cayo.graph.locationElement"
                  ) {
                    const item = v as any;
                    let propWithIcon: any = null;
                    if (item.locationType) {
                      propWithIcon = item.locationType;
                    } else if (v.objectName) {
                      propWithIcon = v.objectName;
                    }

                    const icon = getIconAnnotation(propWithIcon)?.iconId;
                    return icon ? <Icon iconName={icon} /> : null;
                  } else if (icon) {
                    return <Icon iconName={icon.iconId} style={{ color: icon.iconStyle?.color }} />;
                  } else if (possibleValues?.length) {
                    const icon = getIconAnnotation(v.objectType || v.id, true).iconId;
                    return icon ? <Icon iconName={icon} /> : null;
                  }

                  return v.objectType &&
                    !props.selectPropertyName &&
                    props.name === "managedSystemId" ? (
                    <Icon iconName={getIconAnnotation(v.objectType).iconId} />
                  ) : null;
                },
              };
              return result;
            });

            const typeInItem = {
              key: filterText,
              primaryText: filterText,
              imageInitials: "!",
              ValidationState: 1,
              id: filterText,
              link: filterText,
              name: filterText,
              objectName: filterText,
              onRenderPersonaCoin: () => {
                return <OfficeIcon iconName={"CodeEdit"} />;
              },
            };

            if (
              showTypeIn &&
              typeInItem.key &&
              dataResult.find((i) => i.text?.toLowerCase() !== typeInItem.key.toLowerCase())
            ) {
              dataResult = [typeInItem, ...dataResult];
            }

            dataResult?.sort(function (a: any, b: any) {
              const aName = a.text! ?? "";
              const bName = b.text! ?? "";
              if (aName > bName) {
                return 1;
              }
              if (aName < bName) {
                return -1;
              }
              return 0;
            });

            return dataResult;
          }

          return [];
        })
        .catch(() => {
          setIsLoading(false);

          return [];
        });
    },
    [scopes, selectedScopeKey, props.value, fetchPickerObjects]
  );

  const onRemoveSuggestion = (item: IPickerItem): void => {
    const indexPeopleList: number = props.value.indexOf(item);
    const indexMostRecentlyUsed: number = mostRecentlyUsed.indexOf(item);

    if (indexPeopleList >= 0) {
      const newPeople: IPickerItem[] = props.value
        .slice(0, indexPeopleList)
        .concat(props.value.slice(indexPeopleList + 1));
      props.setValue(newPeople, props);
    }

    if (indexMostRecentlyUsed >= 0) {
      const newSuggestedPeople: IPickerItem[] = mostRecentlyUsed
        .slice(0, indexMostRecentlyUsed)
        .concat(mostRecentlyUsed.slice(indexMostRecentlyUsed + 1));
      setMostRecentlyUsed(newSuggestedPeople);
    }
  };

  const onRenderSuggestionsItem = (
    props: IPersonaProps,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    itemProps: ISuggestionItemProps<IPersonaProps>
  ): JSX.Element => {
    return (
      <div className={"ms-Button-flexContainer flexContainer_3696fcf9 flexContainer_9fba0d97"}>
        <PersonaContent className={"ms-PeoplePicker-personaContent"}>
          <Persona {...props} size={PersonaSize.small} styles={{}} />
        </PersonaContent>
      </div>
    );
  };

  const onRenderItem = (
    props: IPickerItemProps<IPersonaProps>,
    removeItem: (id: string) => void
  ): JSX.Element => {
    return (
      <StyledFocusZone
        key={"fz_" + props.index}
        direction={FocusZoneDirection.horizontal}
        handleTabKey={FocusZoneTabbableElements.all}
        isCircularNavigation
        allowFocusRoot
        role="button"
      >
        <div className={"ms-PickerItem-content itemContent_894c3072"}>
          <Persona
            {...props.item}
            size={PersonaSize.small}
            styles={{
              details: { padding: "0 0px 0 6px" },
              root: { height: "unset" },
              textContent: { fontWeight: 600 },
              primaryText: { maxWidth: 374 },
            }}
          />
        </div>
        <div className={"ms-PickerItem-content itemContent_894c3072"}>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            styles={{ icon: { fontSize: 12, fontWeight: 600 }, root: { height: 28 } }}
            ariaLabel="Delete"
            onClick={() => {
              removeItem(props.item.id!);
            }}
          />
        </div>
      </StyledFocusZone>
    );
  };

  const addItems = useCallback(
    (newItems: IPickerItem[]) => {
      let mergedItems = props.multiselect
        ? [...(props.value || []), ...(newItems || [])]
        : newItems;

      if (props.multiselect) {
        mergedItems = arrayUtils.unique<IPickerItem>(mergedItems);
      }

      props.setValue(mergedItems, props);
    },
    [props.value, props.setValue]
  );

  const handleDialogItemsSelected = useCallback(
    (items: Entity[]) => {
      const newItems = items.map<IPickerItem>((v) => {
        const link = valueTemplate
          ? oDataConverters.objectToPickerValue(
              valueTemplate,
              v,
              displayNameProp,
              displayNameAlternateProp
            )
          : props.selectPropertyName
            ? v[props.selectPropertyName]
            : oDataConverters.entityToSoftLink(v);

        const result: IPickerItem = {
          id: v.id!,
          text: (v as any).displayName || v.objectName,
          link,
          onRenderPersonaCoin: () => {
            const icon = (v as any)?.icon as IconAnnotation;
            if (icon) {
              return <Icon iconName={icon.iconId} style={{ color: icon.iconStyle?.color }} />;
            }
            return v.objectType && !props.selectPropertyName && props.name === "managedSystemId" ? (
              <OfficeIcon iconName={getIconAnnotation(v.objectType).iconId} />
            ) : null;
          },
        };
        return result;
      });

      addItems(newItems);

      return Promise.resolve();
    },
    [
      addItems,
      displayNameAlternateProp,
      displayNameProp,
      getIconAnnotation,
      props.name,
      props.selectPropertyName,
      valueTemplate,
    ]
  );

  const openObjectPickerDialog = () => {
    const hideScopeSelection = !props.searchScopes?.length;
    const mappedSearchScopes = mapPropsToSearchScopes(props);

    openById(DIALOG_IDS.OBJECT_PICKER_DIALOG, {
      title: props.modalOptions.title!,
      onSelected: handleDialogItemsSelected,
      onClose: () => undefined,
      // admin props...
      searchScopes: mappedSearchScopes,
      hideScopeSelection,
      keyProp: "id",
      // guardian props...
      id: props.name!,
      columns: props.columns,
      modalOptions: props.modalOptions!,
      path: props.queryPath!,
      defaultOrder: props.defaultOrder,
      filter: props.filter,
      scope: props.scope,
      body: props.body,
      allowMultiselect: props.multiselect,
      hideModificationTimeColumn: props.hideModificationTimeColumn,
      hideObjectTypeIcon: props.hideObjectTypeIcon,
      operator: props.queryOperator,
      fetchObjects: fetchPickerObjects,
    });
  };

  if (readOnly) {
    const defaultValueText =
      props.value?.map((v) => v.text).join("; ") || (props?.defaultValue as any)?.displayName;
    return <Text block={true} renderInput={renderInput} label={label} value={defaultValueText} />;
  }

  return (
    <Column>
      <Row>
        <StyledColumn>
          <Row>
            {props.label ? (
              <Label
                required={props.required}
                label={props.label!}
                // eslint-disable-next-line no-inline-styles/no-inline-styles
                style={{ flex: "1 0 auto" }}
                tooltip={props.tooltip}
              />
            ) : (
              <NoLabel />
            )}
          </Row>

          <PickerWrapper>
            <CompactPeoplePicker
              onResolveSuggestions={(filter, selectedItems) =>
                onFilterChanged(filter, (selectedItems as IPickerItem[]) || [])
              }
              onEmptyResolveSuggestions={() => onFilterChanged("", [])}
              getTextFromItem={getTextFromItem}
              selectedItems={(() => props.value)()}
              pickerSuggestionsProps={suggestionProps}
              className={"ms-PeoplePicker"}
              styles={{
                itemsWrapper: {},
                text: {
                  paddingRight: 30,
                  border:
                    props.error?.errorMessage && `1px solid ${cayoTheme.brandColors.critical}`,
                },
              }}
              onRenderSuggestionsItem={onRenderSuggestionsItem}
              onRenderItem={(p) =>
                onRenderItem(p, (id) => {
                  const index = props?.value?.findIndex((p) => p.id === id);
                  if (index >= 0) {
                    props.value.splice(index, 1);

                    props.setValue([...props.value], props);
                  }
                })
              }
              onRemoveSuggestion={(item) => onRemoveSuggestion(item as IPickerItem)}
              onValidateInput={validateInput}
              inputProps={{
                "aria-label": props.label,
              }}
              componentRef={picker}
              resolveDelay={500}
              disabled={props.disabled}
              onInputChange={(input) => {
                setLastInput(input);
                return input;
              }}
              onBlur={(e) => {
                if (lastInput && !props.offBlurAction) {
                  log.debug(e.target);

                  const event = new Event("input", { bubbles: true });
                  setNativeValue(e.target, "");
                  e.target!.dispatchEvent(event);

                  const result: IPickerItem = {
                    id: lastInput,
                    text: lastInput,
                    link: lastInput,
                  };

                  addItems([result]);
                }
              }}
              onItemSelected={(item) => {
                const pickerItem = item as IPickerItem;
                const key = (item as any)?.key;

                if (key && !item!.id && !props.selectPropertyName && props.columns?.length) {
                  return null;
                }

                if (key) {
                  item!.id = key;
                }

                if (!pickerItem.link) {
                  pickerItem.link = valueTemplate
                    ? oDataConverters.objectToPickerValue(valueTemplate, pickerItem.object)
                    : key;
                  pickerItem.onRenderPersonaCoin = () => {
                    return <OfficeIcon iconName={"CodeEdit"} />;
                  };
                }

                addItems([item] as IPickerItem[]);
                return item as IPersonaProps;
              }}
            />

            {!props.hideMore && !(props.scope?.path && scopes?.length === 0) && (
              <IconButton
                styles={{
                  root: {
                    position: "absolute",
                    right: 1,
                    bottom: -1,
                    height: 30,
                    top: -1,
                    opacity: 0.8,
                    marginTop: 2,
                    marginBottom: 2,
                    backgroundColor: officeTheme.semanticColors.bodyDivider,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }}
                disabled={props.disabled}
                onClick={openObjectPickerDialog}
                iconProps={{ iconName: "More" }}
                data-testid="object-picker-more-btn"
              />
            )}
            {props.showClearButton && props.value?.length > 0 && (
              <IconButton
                disabled={props.disabled}
                iconProps={{
                  iconName: "Cancel",
                }}
                styles={{
                  rootDisabled: {
                    position: "absolute",
                  },
                  root: {
                    position: "absolute",
                    right: 2,
                    bottom: -1,
                    height: 30,
                    top: 2,
                    opacity: 0.8,
                    marginTop: 0,
                    marginBottom: 0,
                    backgroundColor: "transparent",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                  icon: { color: officeTheme.semanticColors.buttonText },
                }}
                onClick={() => {
                  props.setValue([], props);
                }}
              />
            )}
          </PickerWrapper>
        </StyledColumn>
      </Row>
      {props.error?.errorMessage && <Error>{props.error.errorMessage}</Error>}
    </Column>
  );
};

// todo: move to domUtils
function setNativeValue(element: any, value: any) {
  const valueSetter = Object.getOwnPropertyDescriptor!(element, "value")!.set;
  const prototype = Object.getPrototypeOf(element);
  const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, "value")!.set;

  if (valueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter!.call(element, value);
  } else {
    valueSetter!.call(element, value);
  }
}

export default withHidden(ObjectPicker);
